import { withFabricCreationValidation } from '@common/EntityToCreate/FabricCreationValidation';
import { useConnectionSpecContext } from '@common/Fabric/Connections/hooks';
import { getFabricRequiredRoleLabel, useCanEditFabric } from '@common/Fabric/useCanEditFabric';
import { useBackendLanguage } from '@common/language';
import { useUpdateOnboardingStatus } from '@common/onboarding/hooks';
import { getDialogPropsForCreateProject } from '@common/onboarding/ide-utils';
import { useTrailFabricOnboarding } from '@common/onboarding/trailFabric';
import { OnboardingStatus } from '@common/onboarding/types';
import StepperPage from '@common/StepperPage';
import { TabError } from '@common/StepperPage/hooks';
import { FixedHeightStep } from '@common/StepperPage/styles';
import { Private_Routes } from '@common/url';
import { EventType, Onboarding } from '@common/user-analytics/events';
import { useTranspilerImportURL } from '@common/utils/hooks/params';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { EventName, EventType as MetricsEventType, metrics, TrackArgs } from '@prophecy/metrics';
import { Dialog, isFormDirty, Spinner, Stack } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { firstOfSet } from '@prophecy/utils/data';
import { navigateAndRefresh } from '@prophecy/utils/history';
import { useAfterNextRender, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { usePrompt } from '@prophecy/utils/react/router';
import { parseJSON } from '@prophecy/utils/string';
import { get, noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useSupportedFabricProviderTypes } from '../../context/edition';
import { useEnvConfig } from '../../context/envConfig';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useSQLStore } from '../Sql/hooks';
import { AttachFabric } from '../Workflow/AttachFabric/AttachFabric';
import { ComponentRunContext, useRunDataContext } from '../Workflow/AttachFabric/componentRunhook';
import BasicForm from './common/BasicForm';
import FabricCrudWrapper from './common/FabricCrudWrapper';
import { withInitialFabricCopy } from './common/InitialFabricCopyContext';
import ProviderForm from './common/ProviderForm';
import { useTestConnection, withTestConnectionProvider } from './common/TestConnectionContext';
import { getFabricSteps } from './constants';
import { useConnections } from './hooks/useConnections';
import useCreateFabric from './hooks/useCreateFabric';
import useFormContext from './hooks/useFormContext';
import useLoadFabricData from './hooks/useLoadFabricData';
import useUpdateFabric from './hooks/useUpdateFabric';
import { OrchestrationSecretTab } from './secrets/OrchestrationSecretTab';
import { SparkSecretsTab } from './secrets/SparkSecretsTab';
import { FABRIC_TABS, FabricProviderType } from './types';
import { getFabricHeader } from './utils/getFabricHeader';

const event_ok_args: EventType = [
  Onboarding.onboarding_create_project.events.onboarding_project_create,
  {
    onboarding_project_create_from_onboarding: true
  },
  Onboarding.onboarding_create_project.event_category
];
const event_cancel_args: EventType = [
  Onboarding.onboarding_create_project.events.onboarding_project_create,
  {
    onboarding_project_create_onboarding_abort: true
  },
  Onboarding.onboarding_create_project.event_category
];

// Unified-metrics twins of the Mixpanel event tuples above.
const metric_ok_args: TrackArgs = {
  eventName: EventName.ui.onboarding.project.create,
  eventType: MetricsEventType.userAction,
  payload: { onboardingProjectCreateFromOnboarding: true }
};
const metric_cancel_args: TrackArgs = {
  eventName: EventName.ui.onboarding.project.create,
  eventType: MetricsEventType.userAction,
  payload: { onboardingProjectCreateOnboardingAbort: true }
};

const CreateFabric = withFabricCreationValidation(
  withInitialFabricCopy(
    withTestConnectionProvider(() => {
      const [params] = useSearchParams();
      const fabricProviderTypeParam = params.get('fabricProviderType') as FabricProviderType;

      const supportedFabricProviderTypes = useSupportedFabricProviderTypes();
      const _fabricProviderType = useMemo(() => {
        const isFabricProviderTypeValid = supportedFabricProviderTypes.has(fabricProviderTypeParam);

        if (!isFabricProviderTypeValid) {
          if (!!fabricProviderTypeParam) {
            console.error('Invalid Fabric provider type detected in Url', {
              fabricProviderType: fabricProviderTypeParam
            });
          }

          return supportedFabricProviderTypes.has(FabricProviderType.Orchestration)
            ? FabricProviderType.Orchestration
            : firstOfSet(supportedFabricProviderTypes);
        }

        return fabricProviderTypeParam;
      }, [fabricProviderTypeParam, supportedFabricProviderTypes]);

      const { uid: fabricId = '' } =
        useParams<ExtractUrlPathVariables<typeof Private_Routes.Create_Entity.fabric.existing.url>>();
      const isUpdateInCreateMode = Boolean(fabricId);

      const { enabled: isTrailFabricOnboardingEnabled } = useTrailFabricOnboarding(Boolean(fabricId));

      const updateOnboardingStatus = useUpdateOnboardingStatus();

      const navigate = useNavigate();
      const { track } = useMixPanel();
      const globalConf = useGlobalVariables();
      const {
        fabrics: { unifiedFabricUX }
      } = useEnvConfig();

      const [currentTab, setCurrentTab] = useState<string>(FABRIC_TABS.basic);
      const { isTranspiler, transpilerImportURl, teamId, getCompleteUrl } = useTranspilerImportURL();

      const formContexts = useFormContext(fabricId, _fabricProviderType, teamId || '');
      const fabricName = formContexts[FABRIC_TABS.basic].watch('name');
      const fabricProviderType = formContexts[FABRIC_TABS.providers].watch('fabricProviderType');
      const fabricProviderTypesV2 = formContexts[FABRIC_TABS.providers].watch('fabricProviderTypesV2');
      const fabricData = formContexts[FABRIC_TABS.providers].watch('fabricData');
      const sqlProviderType = formContexts[FABRIC_TABS.providers].watch('sqlProvider.providerInfo.providerType');
      const effectiveFabricId = formContexts[FABRIC_TABS.basic].watch('fabricId');
      const selectedTeamId = formContexts[FABRIC_TABS.basic].watch('ownerUID');
      const provider = formContexts[FABRIC_TABS.providers].watch('provider');

      // Sync provider to ConnectionSpecProvider
      const { setProviderType } = useConnectionSpecContext();
      useEffect(() => {
        setProviderType(provider);
      }, [provider, setProviderType]);

      const hasFabricProvider = usePersistentCallback((fabricProvider: FabricProviderType) => {
        // check old UX -> fabricProviderType is the only provider type
        if (!fabricProviderTypesV2 || !unifiedFabricUX) return fabricProviderType === fabricProvider;

        // check new UX -> fabricProviderTypesV2 is the array of provider types
        return fabricProviderTypesV2.includes(fabricProvider);
      });

      const {
        actions: { hasFabricCrudOperationAccess }
      } = useCanEditFabric();
      const { isLoading: isFabricLoading } = useLoadFabricData(fabricId, formContexts, isUpdateInCreateMode);

      const { createNewFabric, isLoading: isCreateLoading } = useCreateFabric(
        formContexts,
        !isTrailFabricOnboardingEnabled
      );
      const { updateExistingFabric, isLoading: isUpdateLoading } = useUpdateFabric(fabricId, formContexts, true);

      const connectionContext = useConnections(formContexts);
      const { FormComponent, formContext: connectionFormContext } = connectionContext;

      const onCancel = useCallback(async () => {
        if (isTrailFabricOnboardingEnabled) {
          const skipFabricOnboardingDialogResult = await Dialog.warning({
            'data-test-id': 'skip-fabric-onboarding-warning-dialog',
            title: `Do you wish to skip Fabric onboarding ?`,
            okButton: { children: 'Yes', danger: true },
            closeButton: { children: 'No' }
          });
          if (skipFabricOnboardingDialogResult === Dialog.ResponseAction.okButton) {
            const event_cancel_args: EventType = [
              Onboarding.onboarding_create_fabric.events.onboarding_fabric_create,
              {
                onboarding_fabric_create_onboarding_abort: true
              },
              Onboarding.onboarding_create_fabric.event_category
            ];
            track(...event_cancel_args);
            metrics.track({
              eventName: EventName.ui.onboarding.fabric.create,
              eventType: MetricsEventType.userAction,
              payload: { fabricCreationAborted: true }
            });
            navigate(Private_Routes.Create_Entity.home.getUrl());
          }
        } else if (isTranspiler) {
          navigate(transpilerImportURl);
        } else {
          navigate(Private_Routes.Create_Entity.home.getUrl());
        }
      }, [isTrailFabricOnboardingEnabled, transpilerImportURl, track, navigate, isTranspiler]);

      const getTabHeader = (key: string) =>
        getFabricHeader(key as FABRIC_TABS, formContexts, connectionContext, { isAllowed: true, tooltip: undefined });

      const getStepContent = (key: string, setErrors: (hasErrors: TabError) => void) => {
        switch (key) {
          case FABRIC_TABS.basic:
            return (
              <BasicForm
                formContext={formContexts[key]}
                updateFabricErrors={setErrors}
                fabricId={fabricId || undefined}
              />
            );
          case FABRIC_TABS.providers:
            return (
              <ProviderForm
                fabricId={fabricId || undefined}
                disableProvider={Boolean(fabricId)}
                formContext={formContexts[key]}
                teamId={selectedTeamId}
                updateFabricErrors={setErrors}
                isCreateMode
              />
            );
          case FABRIC_TABS.connections:
            return (
              <FixedHeightStep>
                <FormComponent formContexts={formContexts} />
              </FixedHeightStep>
            );
          case FABRIC_TABS.secrets:
            if (hasFabricProvider(FabricProviderType.Orchestration)) {
              return <OrchestrationSecretTab />;
            }
            return globalConf.isSecretManagerEnabled &&
              fabricData &&
              hasFabricProvider(FabricProviderType.Spark) &&
              effectiveFabricId ? (
              <FixedHeightStep>
                <SparkSecretsTab
                  isUnifiedOrchestrationFabric={false}
                  formContexts={formContexts}
                  fabricId={effectiveFabricId}
                />
              </FixedHeightStep>
            ) : null;
        }
      };

      const { testConnection, isLoading: testConnectionLoading, shouldTestConnection } = useTestConnection();
      const [removeUnblockPrompt, setRemoveUnblockPrompt] = useState(false);

      const { afterNextRender } = useAfterNextRender();

      const onCompleteHandler = () => {
        afterNextRender(async () => {
          if (isTrailFabricOnboardingEnabled) {
            const dialogResult = await Dialog.success(getDialogPropsForCreateProject(true, false, false));
            if (dialogResult === Dialog.ResponseAction.okButton) {
              track(...event_ok_args);
              metrics.track(metric_ok_args);
              navigateAndRefresh(
                Private_Routes.Create_Entity.project.getUrl(undefined, {
                  onboarding: 'true',
                  providerType: hasFabricProvider(FabricProviderType.SQL) ? sqlProviderType : undefined
                })
              );
              return true;
            } else {
              track(...event_cancel_args);
              metrics.track(metric_cancel_args);
            }
          } else if (isTranspiler) {
            const id = formContexts[FABRIC_TABS.basic].getValues('fabricId');
            navigate(getCompleteUrl(id as string, 'fabricId'));
            return;
          }

          const fabricId = formContexts[FABRIC_TABS.basic].getValues('fabricId');

          if (!globalConf.getFabricStatus) {
            navigateAndRefresh(Private_Routes.Home.getUrl());
          } else if (fabricId) {
            navigate(Private_Routes.Fabric.home.getUrl({ uid: fabricId }));
          } else {
            navigateAndRefresh(Private_Routes.User.tab.getUrl({ tab: 'fabrics' }));
          }
        });
      };
      const isOrchestrationType = hasFabricProvider(FabricProviderType.Orchestration);

      const createFabric = async () => {
        if (shouldTestConnection && !(await testConnection())) return false;

        if (fabricId) {
          await updateExistingFabric();
        } else {
          const fabricResponse = await createNewFabric();
          const fabricId =
            hasFabricProvider(FabricProviderType.Spark) && !unifiedFabricUX
              ? get(parseJSON(fabricResponse, {}), 'data.Fabric._id')
              : fabricResponse;

          if (isOrchestrationType) {
            setRemoveUnblockPrompt(true);
          }

          formContexts[FABRIC_TABS.basic].setValue('fabricId', fabricId);
        }

        if (isTrailFabricOnboardingEnabled) {
          const status = OnboardingStatus.NEW_FABRIC;
          const payload = hasFabricProvider(FabricProviderType.SQL)
            ? { lastFlowCompletedSql: status }
            : { lastFlowCompleted: status };
          await updateOnboardingStatus(payload);
        }

        return true;
      };

      const onNextHandler = async (key: string): Promise<boolean> => {
        const formContext = key === FABRIC_TABS.connections ? connectionFormContext : formContexts[key as FABRIC_TABS];
        let canContinue: boolean | Promise<boolean> = !formContext;
        await formContext?.handleSubmit(async (data) => {
          const { allowed: hasFabricEditAccess, reason: fabricEditReason } = hasFabricCrudOperationAccess(
            (data as { ownerUID: string })?.ownerUID || selectedTeamId,
            // false is sent to ignore orchestration check as this is fabric creation flow
            false
          );
          if (!hasFabricEditAccess) {
            toast.error({
              content: `Only ${getFabricRequiredRoleLabel(fabricEditReason)}s can create the fabric.`
            });
            canContinue = false;
          } else if (key === FABRIC_TABS.providers) {
            canContinue = await createFabric();
          } else if (key === FABRIC_TABS.connections && isOrchestrationType) {
            const connection = formContexts[FABRIC_TABS.orchestrationConnections].watch('defaultConnection');
            if (!connection) {
              toast.error({
                content: 'Please create a SQL Warehouse connection'
              });
              canContinue = false;
            } else {
              canContinue = true;
            }
          } else {
            canContinue = true;
          }
        })();

        return canContinue;
      };

      usePrompt(
        () => {
          return 'Do you want to leave this page without saving your changes?';
        },
        !removeUnblockPrompt && isFormDirty(formContexts[FABRIC_TABS.basic])
      );
      const runDataContextValue = useRunDataContext();
      const language = useBackendLanguage();
      return (
        <>
          {fabricId && isFabricLoading ? (
            <Stack height='100%' align='center' alignY='center'>
              <Spinner spinning />
            </Stack>
          ) : (
            <StepperPage
              steps={getFabricSteps({
                fabricProviderType,
                isSecretManagerEnabled: globalConf.isSecretManagerEnabled,
                unifiedFabricUX,
                provider
              })}
              getStepContent={getStepContent}
              getTabHeader={getTabHeader}
              onCancel={onCancel}
              isLoading={testConnectionLoading || (fabricId ? isUpdateLoading : isCreateLoading)}
              pageHeaderProps={{
                crumbs: [
                  {
                    category: 'Fabric',
                    label: fabricName || `Untitled Fabric`
                  }
                ],
                children:
                  hasFabricProvider(FabricProviderType.Spark) && currentTab === FABRIC_TABS.secrets ? (
                    <ComponentRunContext.Provider value={runDataContextValue}>
                      <AttachFabric
                        allowFabricSelection={false}
                        setSessionUsed={noop}
                        hidden={false}
                        disabled={false}
                        fabricId={effectiveFabricId}
                        setFabric={noop}
                        attachmentSource='Secrets'
                        isPipelineOnboardingEnabled={false}
                        language={language}
                      />
                    </ComponentRunContext.Provider>
                  ) : null
              }}
              okButton={fabricId ? 'Complete Setup' : undefined}
              onNext={onNextHandler}
              onComplete={onCompleteHandler}
              onStepChange={(step) => {
                setCurrentTab(step.key);
              }}
            />
          )}
        </>
      );
    })
  )
);

const WrappedCreateFabric = () => {
  const store = useSQLStore('');
  return (
    <FabricCrudWrapper enableAdminJwt store={store}>
      <CreateFabric />
    </FabricCrudWrapper>
  );
};

export default WrappedCreateFabric;
